export type RecordingStatus = "inactive" | "recording" | "paused";

export interface MicrophoneOption {
  deviceId: string;
  label?: string;
}

export function toVolumePercent(level: number): number {
  if (!Number.isFinite(level)) {
    return 0;
  }
  return Math.round(Math.min(Math.max(level, 0), 1) * 100);
}

export function formatMicrophoneLabel(device: MicrophoneOption): string {
  return device.label || `Microphone ${device.deviceId.slice(0, 5)}`;
}

export function getStartRecordingLabel(isStarting: boolean): string {
  return isStarting ? "Starting..." : "Start Recording";
}

export function getAudioTestLabel(isStarting: boolean, isActive: boolean): string {
  if (isStarting) {
    return "Starting test…";
  }
  return isActive ? "Stop test" : "Test Audio";
}

export function isStartRecordingDisabled({
  isStartingRecording,
  isAudioTestStarting,
  isAudioTestActive,
}: {
  isStartingRecording: boolean;
  isAudioTestStarting: boolean;
  isAudioTestActive: boolean;
}): boolean {
  return isStartingRecording || isAudioTestStarting || isAudioTestActive;
}

export function shouldShowStartRecording({
  permission,
  recordingStatus,
  hasRecorded,
  isMergingChunks,
}: {
  permission: boolean;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
  isMergingChunks: boolean;
}): boolean {
  return permission && recordingStatus === "inactive" && !hasRecorded && !isMergingChunks;
}

export function shouldShowReturnHome({
  recordingStatus,
  hasRecorded,
  isMergingChunks,
  isAudio,
  isStartingRecording,
}: {
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
  isMergingChunks: boolean;
  isAudio: boolean;
  isStartingRecording: boolean;
}): boolean {
  return (
    recordingStatus === "inactive" &&
    !hasRecorded &&
    !isMergingChunks &&
    !isAudio &&
    !isStartingRecording
  );
}

export function shouldShowIdleMicrophoneTools({
  permission,
  recordingStatus,
  hasRecorded,
}: {
  permission: boolean;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
}): boolean {
  return permission && recordingStatus === "inactive" && !hasRecorded;
}

export function shouldShowConsentMessage({
  org,
  recordingStatus,
  hasRecorded,
}: {
  org?: string | null;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
}): boolean {
  return org === "VA" && recordingStatus === "inactive" && !hasRecorded;
}

export function canStopRecording(elapsedSeconds: number, minimumSeconds: number): boolean {
  return Number.isFinite(elapsedSeconds) && elapsedSeconds >= minimumSeconds;
}

export function shouldShowTimer(recordingStatus: RecordingStatus, hasRecorded: boolean): boolean {
  return recordingStatus === "recording" || recordingStatus === "paused" || hasRecorded;
}

/**
 * Text for the recorder's polite live region. The ticking timer is left out on
 * purpose: announcing it every second would drown out everything else.
 */
export function getRecordingStatusAnnouncement({
  recordingStatus,
  hasRecorded,
  isLoading,
  readyToUpload,
}: {
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
  isLoading: boolean;
  readyToUpload: boolean;
}): string {
  if (recordingStatus === "recording") {
    return "Recording";
  }
  if (recordingStatus === "paused") {
    return "Recording paused";
  }
  if (isLoading) {
    return "Recording stopped. Processing audio.";
  }
  if (readyToUpload) {
    return "Recording ready to upload.";
  }
  return hasRecorded ? "Recording stopped." : "";
}

export function shouldShowVolumeMeters({
  recordingStatus,
  isAudioTestActive,
}: {
  recordingStatus: RecordingStatus;
  isAudioTestActive: boolean;
}): boolean {
  return recordingStatus === "recording" || recordingStatus === "paused" || isAudioTestActive;
}

export function shouldShowPatientVolumeMeter({
  recordingStatus,
  isAudioTestActive,
  hasTelehealthScreenAudio,
}: {
  recordingStatus: RecordingStatus;
  isAudioTestActive: boolean;
  hasTelehealthScreenAudio: boolean;
}): boolean {
  return (
    recordingStatus === "recording" ||
    recordingStatus === "paused" ||
    (isAudioTestActive && hasTelehealthScreenAudio)
  );
}

export function shouldShowFloatingIndicator({
  recordingStatus,
  hasFloatingRecordingPopup,
  isSupported,
  isOpen,
  isOpening,
}: {
  recordingStatus: RecordingStatus;
  hasFloatingRecordingPopup: boolean;
  isSupported: boolean;
  isOpen: boolean;
  isOpening: boolean;
}): boolean {
  return (
    (recordingStatus === "recording" || recordingStatus === "paused") &&
    hasFloatingRecordingPopup &&
    isSupported &&
    !isOpen &&
    !isOpening
  );
}
